import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";
import type { SalesOrderItem } from "../../UI/CreateOrder/CreateSalesOrder";

interface ProductSelectorModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (item: SalesOrderItem) => void;
  warehouseId: number | null;
  selectedPartIds: Set<number>;
  initialSearch?: string;
}

const PAGE_SIZE = 50;

const ProductSelectorModal: React.FC<ProductSelectorModalProps> = ({
  open,
  onClose,
  onSelect,
  warehouseId,
  selectedPartIds,
  initialSearch = "",
}) => {
  const [search, setSearch] = useState(initialSearch);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [parts, setParts] = useState<SalesOrderItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [selected, setSelected] = useState<SalesOrderItem | null>(null);
  const prevOpen = useRef(false);
  const loadingRef = useRef(loading);
  const pageRef = useRef(page);

  const fetchParts = useCallback(
    async (pageNum: number, searchTerm: string, append = false) => {
      if (!warehouseId) return;
      setLoading(true);
      loadingRef.current = true;
      try {
        const response = await AllPartApi.api.getPartList(
          pageNum,
          PAGE_SIZE,
          searchTerm,
          undefined,
          undefined,
          undefined,
          warehouseId,
          undefined,
          true
        );
        const availableParts = response.data.data.filter(
          (part: any) => !selectedPartIds.has(part.id)
        );
        const mapped = availableParts.map((part: any) => ({
          ...part,
          productId: part.id,
          productName: part.description,
          quantity: 0,
          price: part.salesPrice,
          tax: part.tax,
          availableQuantity: part.quantity,
          isOutOfStock: part.isOutOfStock || false,
        }));
        setParts((prev) => (append ? [...prev, ...mapped] : mapped));
        setHasMore(pageNum < response.data.totalPages - 1);
        setPage(pageNum);
        pageRef.current = pageNum;
      } catch (e) {
        setParts([]);
        setHasMore(false);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [warehouseId, selectedPartIds]
  );

  // Set search to initialSearch only when modal transitions from closed to open
  useEffect(() => {
    if (open && !prevOpen.current && warehouseId) {
    
      console.log("Modal opened with initialSearch:", initialSearch);
      setSearch(initialSearch);
      setPage(0);
      setParts([]);
      setHasMore(false);
      setSelected(null);
      fetchParts(0, initialSearch, false);
      pageRef.current = 0;
      loadingRef.current = false;
    }
    prevOpen.current = open;
    // eslint-disable-next-line
  }, [open, warehouseId, selectedPartIds, initialSearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    fetchParts(0, e.target.value, false);
  };

  // Infinite scroll handler
  const handleTableScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (
      scrollTop + clientHeight >= scrollHeight - 30 &&
      hasMore &&
      !loadingRef.current
    ) {
      fetchParts(pageRef.current + 1, search, true);
    }
  };

  const handleRowClick = (item: SalesOrderItem) => {
    setSelected(item);
  };

  const handleSelect = () => {
    if (selected) {
      onSelect(selected);
      onClose();
    }
  };

  const handleLoadMore = () => {
    fetchParts(page + 1, search, true);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#fff",
          borderBottom: 1,
          borderColor: "divider",
          py: 2,
        }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#1E1E1E" }}>
          Select Product
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "#1E1E1E" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 1,
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "#fff",
            borderBottom: 1,
            borderColor: "divider",
          }}>
          <TextField
            fullWidth
            placeholder="Search product by name, code, etc."
            value={search}
            onChange={handleSearchChange}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#1E1E1E" }} />
                </InputAdornment>
              ),
              style: { color: "#1E1E1E" },
            }}
            sx={{
              bgcolor: "#fff",
              input: { color: "#1E1E1E" },
            }}
          />
        </Box>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            height: 500,
            maxHeight: 500,
            border: 0,
            boxShadow: "none",
            bgcolor: "#fff",
            overflowY: "auto",
          }}
          onScroll={handleTableScroll}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: "#fff" }}>
                <TableCell sx={{ fontWeight: 700, color: "#1E1E1E" }}>
                  Name
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#1E1E1E" }}>
                  Available Qty
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#1E1E1E" }}>
                  Unit Price (₹)
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#1E1E1E" }}>
                  Tax (%)
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#1E1E1E" }}>
                  Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parts.map((item) => {
                const isSelected = selected?.productId === item.productId;
                const selectedTextColor = isSelected ? "#000000" : "#1E1E1E";
                return (
                  <TableRow
                    key={item.productId}
                    hover
                    selected={isSelected}
                    onClick={() => handleRowClick(item)}
                    sx={{
                      cursor: item.isOutOfStock ? "not-allowed" : "pointer",
                      opacity: item.isOutOfStock ? 0.5 : 1,
                      backgroundColor: isSelected ? "#1E1E1E" : undefined,
                      transition: "background 0.2s",
                      "&:hover": {
                        backgroundColor: item.isOutOfStock
                          ? undefined
                          : "#f5f5f5",
                      },
                    }}>
                    <TableCell
                      sx={{ color: selectedTextColor, fontWeight: 500 }}>
                      {item.productName}
                    </TableCell>
                    <TableCell
                      sx={{ color: selectedTextColor, fontWeight: 500 }}>
                      {item.availableQuantity}
                    </TableCell>
                    <TableCell
                      sx={{ color: selectedTextColor, fontWeight: 500 }}>
                      {item.price}
                    </TableCell>
                    <TableCell
                      sx={{ color: selectedTextColor, fontWeight: 500 }}>
                      {item.tax}
                    </TableCell>
                    <TableCell>
                      {item.isOutOfStock ? (
                        <Typography
                          color="error"
                          fontWeight={600}
                          fontSize={13}
                          sx={{ color: isSelected ? "#fff" : "#d32f2f" }}>
                          Out of Stock
                        </Typography>
                      ) : (
                        <Typography
                          sx={{
                            color: selectedTextColor,
                            fontWeight: 600,
                            fontSize: 13,
                          }}>
                          Available
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && parts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No products found. Try a different search or check your
                      filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          bgcolor: "#fff",
          borderTop: 1,
          borderColor: "divider",
        }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: 4,
            textTransform: "none",
            color: "#1E1E1E",
            borderColor: "#1E1E1E",
          }}
          variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSelect}
          variant="contained"
          sx={{
            borderRadius: 4,
            px: 4,
            fontWeight: 700,
            textTransform: "none",
            boxShadow: "none",
            bgcolor: "#1E1E1E",
            color: "#fff",
            "&:hover": { bgcolor: "#000", color: "#fff" },
          }}
          disabled={!selected || selected.isOutOfStock}>
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductSelectorModal;
